/**
 * ContractVariableButton - Botão dropdown para inserir variáveis no editor
 * 
 * Exibe lista de variáveis pré-definidas organizadas por origem
 * Inclui opção especial para inserir tabela de produtos com loop
 * Ao clicar, insere {{variavel}} na posição do cursor do editor TipTap
 * 
 * @release v1.1.0 - 2026-02-17
 */

import { useState } from 'react';
import { Variable, TableProperties, Wrench, LayoutGrid, Calculator, Car } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';

import { Badge } from '@/components/ui/badge';
import {
  PREDEFINED_VARIABLES,
  PRODUCT_TABLE_TEMPLATE,
  SERVICES_TABLE_TEMPLATE,
  FULL_ITEMS_TABLE_TEMPLATE,
  TOTALS_SUMMARY_TEMPLATE,
  VEHICLES_TABLE_TEMPLATE,
  type ContractVariable,
} from '@/types/contracts';

// Props do componente
interface ContractVariableButtonProps {
  /** Callback ao selecionar variável (texto a inserir) */
  onInsert: (variable: string) => void;
}

/**
 * Agrupa variáveis por origem (contact, deal, tenant, custom)
 */
const SOURCE_LABELS: Record<string, string> = {
  contact: 'Cliente / Contato',
  deal: 'Negócio',
  tenant: 'Empresa',
  custom: 'Sistema',
  vehicles: 'Veículos',
};

export function ContractVariableButton({ onInsert }: ContractVariableButtonProps) {
  const [open, setOpen] = useState(false);

  // Agrupa variáveis por origem (exclui type 'list' pois são tratadas pelo loop)
  const groupedVariables = PREDEFINED_VARIABLES
    .filter((v) => v.type !== 'list')
    .reduce<Record<string, ContractVariable[]>>(
      (acc, variable) => {
        // v1.2.0: variáveis de veículo ganham grupo próprio no menu
        const group = variable.group === 'vehicles' ? 'vehicles' : variable.source;
        if (!acc[group]) acc[group] = [];
        acc[group].push(variable);
        return acc;
      },
      {}
    );

  // Handler para inserir variável simples
  const handleInsert = (key: string) => {
    onInsert(`{{${key}}}`);
    setOpen(false);
  };

  // Handler para inserir um snippet HTML completo (tabela de produtos, serviços, etc.)
  const handleInsertSnippet = (snippet: string) => {
    onInsert(snippet);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <Variable className="h-4 w-4" />
          <span>Variáveis</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <div className="p-3 border-b">
          <p className="text-sm font-medium">Inserir Variável</p>
          <p className="text-xs text-muted-foreground mt-1">
            Clique para inserir no editor. Será preenchida automaticamente com dados do negócio.
          </p>
        </div>
        {/* div nativo para scroll - resolve conflito Radix ScrollArea + Popover */}
        <div 
          className="max-h-72 overflow-y-auto overscroll-contain"
          onWheel={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
        >
          <div className="p-2 space-y-3">
            {/* Snippets especiais: tabelas e resumo de totais (com loop {{#produtos}}) */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1">
                Tabelas e Resumos
              </p>
              <div className="space-y-1">
                <button
                  onClick={() => handleInsertSnippet(PRODUCT_TABLE_TEMPLATE)}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left border border-dashed border-border"
                >
                  <TableProperties className="h-4 w-4 text-amber-500 shrink-0" />
                  <div>
                    <span className="font-medium">Tabela de Produtos</span>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Colunas básicas: SKU, Qtd, Preço, Desconto, Total
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => handleInsertSnippet(SERVICES_TABLE_TEMPLATE)}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left border border-dashed border-border"
                >
                  <Wrench className="h-4 w-4 text-blue-500 shrink-0" />
                  <div>
                    <span className="font-medium">Tabela de Serviços</span>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Inclui Mensalidade e Prazo (avulso/recorrente)
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => handleInsertSnippet(FULL_ITEMS_TABLE_TEMPLATE)}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left border border-dashed border-border"
                >
                  <LayoutGrid className="h-4 w-4 text-primary shrink-0" />
                  <div>
                    <span className="font-medium">Tabela Completa (Produtos + Serviços)</span>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Espelha 100% a aba Produtos do negócio
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => handleInsertSnippet(VEHICLES_TABLE_TEMPLATE)}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left border border-dashed border-border"
                >
                  <Car className="h-4 w-4 text-sky-500 shrink-0" />
                  <div>
                    <span className="font-medium">Tabela de Veículos</span>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Placa, Tipo, Marca/Modelo, Ano, Cor e Valor FIPE
                    </p>
                  </div>
                </button>
                <button
                  onClick={() => handleInsertSnippet(TOTALS_SUMMARY_TEMPLATE)}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-muted transition-colors text-left border border-dashed border-border"
                >
                  <Calculator className="h-4 w-4 text-emerald-500 shrink-0" />
                  <div>
                    <span className="font-medium">Resumo de Totais</span>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Subtotal, Desconto, Total, MRR e Vigência
                    </p>
                  </div>
                </button>
              </div>
            </div>


            <Separator />

            {/* Variáveis simples agrupadas por origem */}
            {Object.entries(groupedVariables).map(([source, variables]) => (
              <div key={source}>
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 mb-1">
                  {SOURCE_LABELS[source] || source}
                </p>
                <div className="space-y-0.5">
                  {variables.map((variable) => (
                    <button
                      key={variable.key}
                      onClick={() => handleInsert(variable.key)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-muted transition-colors text-left"
                    >
                      <span className="truncate">{variable.label}</span>
                      <Badge variant="outline" className="text-[10px] font-mono shrink-0">
                        {`{{${variable.key}}}`}
                      </Badge>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}